/**
 * Financeiro no modo ao vivo: DRE com os pedidos reais e a mídia da Meta, valor do comprador e a
 * economia (teto de CPA, verba de teste, alvos de CPA, ROAS e MER) medidos nas vendas reais.
 *
 * Regra do dono (28/10/2026): o anúncio é pago com o faturamento inteiro, então o valor do comprador
 * é o que fica na empresa — valor dos produtos (curso + 20X) menos a taxa da Kiwify, por comprador do
 * curso — e não a nossa comissão. O teto real desconta o reembolso medido (28 dias) e o custo real
 * da mídia (o fator custo ÷ gasto que o servidor calcula: CA01 com 12,15% por dentro, YO98 com PTAX +
 * spread + IOF) — a mesma conta do "Resultado da empresa" da DRE, que fecha sem imposto sobre a
 * receita. Nada daqui sai da simulação: sem comprador, o valor fica vazio, com o motivo.
 *
 * DRE do modo ao vivo (dono, 28/10): fecha no "Resultado da empresa" = valor dos produtos − taxa da
 * Kiwify − reembolsos − mídia com imposto. A divisão com os coprodutores (nossa comissão e a parte
 * deles, medidas nos pedidos) e os juros do parcelamento vêm à parte, só como informação. Imposto
 * sobre a receita só entra se o dono cadastrar a taxa; despesas fixas, só as cadastradas (rateio).
 * Lucro em risco e caixa saem dos pedidos reais; o que a Kiwify não manda (saldo) fica "não medido".
 */
import type {
  BuyerValue,
  ColumnarSeries,
  EconomicsV2,
  Expense,
  FinanceQuery,
  FinanceReport,
  GoalProgress,
  KpiId,
  KpiValue,
  KpiValueV2,
  PainelProviderV2,
  PnL,
  Query,
  SeriesQuery,
  UnitEconomics,
} from '@rota/contracts';
import { offMetaDims } from '../metaCut';
import {
  BRT,
  buckets,
  compareMs,
  cumulative,
  DAY,
  dayStart,
  HOUR,
  isPaid,
  isRefundLike,
  kv,
  matches,
  noRealKpi,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  revenueOf,
  row0,
  setKpiV2,
  type Totals,
  todayKey,
} from './realCore';
import { buildLiveGoals, goalHistory } from './realGoals';
import { ENTITY_SALES_NOTE, mediaMissing, OFF_META_NOTE, type RealMedia } from './realMedia';
import { hasEntityFilter } from './realTraffic';

type GetEconomics = NonNullable<PainelProviderV2['getEconomics']>;
type Economics = Awaited<ReturnType<GetEconomics>>;

// ---------- contas puras (a fronteira 10.2 não deixa o app importar `@rota/metrics`) ----------

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const pct1 = (r: number) => `${(r * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** Vendas pagas do curso e do 20X: quantas e o que fica na empresa (valor dos produtos − taxa). */
export interface BuyerBase {
  front: number;
  upsell: number;
  /** Σ (valor do produto − taxa da Kiwify) dos cursos pagos. */
  frontValue: number;
  upsellValue: number;
  /** Σ valor dos produtos (curso + 20X), sem os juros do parcelamento. */
  products: number;
}

/** O que fica na empresa por pedido: valor do produto (sem juros) menos a taxa da Kiwify. */
export const companyValueOf = (o: RealOrderRow): number => revenueOf(o) - (o.feeCents ?? 0);

export function buyerBase(rows: readonly RealOrderRow[]): BuyerBase {
  const b: BuyerBase = { front: 0, upsell: 0, frontValue: 0, upsellValue: 0, products: 0 };
  for (const o of rows) {
    if (!isPaid(o) || (o.kind !== 'front' && o.kind !== 'upsell')) continue;
    b.products += revenueOf(o);
    if (o.kind === 'front') {
      b.front++;
      b.frontValue += companyValueOf(o);
    } else {
      b.upsell++;
      b.upsellValue += companyValueOf(o);
    }
  }
  return b;
}

/** Valor do comprador: (valor dos produtos − taxa da Kiwify) do curso e do 20X ÷ compradores do curso. */
export const companyBuyerValue = (b: BuyerBase): number | null =>
  b.front > 0 ? Math.round((b.frontValue + b.upsellValue) / b.front) : null;

/** Contribuição do 20X: o que o 20X deixa na empresa ÷ o que o curso deixa (u × 20X ÷ curso). */
export const upsellShare = (b: BuyerBase): number | null =>
  b.front > 0 && b.frontValue > 0 ? b.upsellValue / b.frontValue : null;

/** Intervalo de Wilson (IC 90%: z = 1,645), a mesma conta do `wilson` de `@rota/metrics`. */
export function wilson90(x: number, n: number): [number, number] {
  if (!(n > 0)) return [0, 1];
  const z = 1.6448536269514722;
  const p = Math.min(1, Math.max(0, x / n));
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const center = (p + z2 / (2 * n)) / denom;
  const half = (z * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n))) / denom;
  return [Math.max(0, center - half), Math.min(1, center + half)];
}

/** Entradas da régua: tudo medido nas vendas e na mídia reais (ou o padrão dos Ajustes, rotulado). */
export interface RulerInput {
  base: BuyerBase;
  /** Reembolsos e chargebacks ÷ (pagos + reembolsados), 28 dias. */
  refundRate: number;
  /** Custo real ÷ gasto da Meta (com imposto, câmbio e IOF). */
  mediaMult: number;
  /** Margem-alvo (Ajustes › Metas). */
  targetMargin: number;
  ceilingBasis: EconomicsV2['cpaCeilingBasis'];
}

/** A régua real: tetos e alvos de CPA, ROAS (valor ÷ gasto), MER e ROAS real. */
export interface Ruler {
  /** Valor do comprador no ato: (produtos − taxa) ÷ compradores do curso. */
  atSale: number;
  /** Valor dos produtos por comprador do curso (curso + u × 20X, sem juros). */
  aov: number;
  /** O que fica por comprador depois dos reembolsos (antes da mídia). */
  contribution: number;
  ceilingAtSale: number;
  /** Teto real: o gasto por comprador que zera o resultado da empresa (reembolsos e custo da mídia). */
  ceilingReal: number;
  cpaBreakEven: number;
  /** Gasto por comprador que deixa a margem-alvo; null quando a margem come tudo. */
  cpaTarget: number | null;
  roasBreakEven: number | null;
  roasTarget: number | null;
  merBreakEven: number | null;
  merTarget: number | null;
  roasRealTarget: number | null;
}

/**
 * Régua do modo ao vivo. CPA e ROAS de empate são os do resultado da empresa, a mesma conta da DRE: o
 * gasto (sem imposto) por comprador que zera produtos − taxa − reembolsos − custo da mídia (com
 * imposto); ROAS = valor ÷ gasto (como o "Purchase ROAS" do Gerenciador). O imposto sobre a receita
 * dos Ajustes não entra (a DRE ao vivo fecha sem ele). Os alvos guardam a margem-alvo sobre o
 * faturamento. MER (faturamento ÷ custo) e ROAS real (resultado antes da mídia ÷ custo) usam a mesma
 * base da DRE, para a cor bater com o "Resultado da empresa".
 */
export function deriveRuler(i: RulerInput): Ruler | null {
  const atSale = companyBuyerValue(i.base);
  if (atSale == null) return null;
  const aov = Math.round(i.base.products / i.base.front);
  const contribution = atSale * (1 - i.refundRate);
  const mult = i.mediaMult > 0 ? i.mediaMult : 1;
  const ceilingReal = Math.round(contribution / mult);
  const afterMargin = contribution - i.targetMargin * aov;
  const cpaTarget = afterMargin > 0 ? Math.round(afterMargin / mult) : null;
  const ratio = (a: number, b: number | null) => (b != null && b > 0 ? a / b : null);
  return {
    atSale,
    aov,
    contribution: Math.round(contribution),
    ceilingAtSale: i.ceilingBasis === 'contribuicao' ? ceilingReal : atSale,
    ceilingReal,
    cpaBreakEven: ceilingReal,
    cpaTarget,
    roasBreakEven: ratio(aov, ceilingReal),
    roasTarget: ratio(aov, cpaTarget),
    merBreakEven: ratio(aov, contribution),
    merTarget: afterMargin > 0 ? aov / afterMargin : null,
    roasRealTarget: afterMargin > 0 ? contribution / afterMargin : null,
  };
}

// ---------- DRE, lucro em risco e caixa (contas puras) ----------

/** Despesa ativa num dia de São Paulo: mensal desde o início; única, no mês em que entra (o rateio da FIN-06). */
const expenseActiveOn = (e: Pick<Expense, 'recurrence' | 'startsOn'>, key: string): boolean =>
  e.recurrence === 'once' ? key.slice(0, 7) === e.startsOn.slice(0, 7) : e.startsOn <= key;

/**
 * Despesas fixas cadastradas no recorte, pelo rateio diário de cada uma (o mesmo da tabela da FIN-06):
 * dia inteiro conta o rateio inteiro; o dia de hoje (ou o último dia de um recorte que para agora), a
 * fração do dia já passada em São Paulo.
 */
export function fixedCostsIn(
  expenses: readonly Pick<Expense, 'recurrence' | 'startsOn' | 'dailyProrated'>[],
  from: number,
  to: number,
): number {
  if (expenses.length === 0 || !(to > from)) return 0;
  let total = 0;
  for (let d = dayStart(todayKey(from)); d < to; d += DAY) {
    const share = (Math.min(to, d + DAY) - Math.max(from, d)) / DAY;
    if (share <= 0) continue;
    const key = todayKey(d);
    for (const e of expenses) if (expenseActiveOn(e, key)) total += e.dailyProrated * share;
  }
  return Math.round(total);
}

/**
 * Divisão da coprodução nas vendas pagas do período (só informação, fora do resultado da empresa):
 * a nossa comissão é a da Kiwify; a parte dos coprodutores é o que sobra do valor dos produtos depois
 * da taxa e da nossa comissão; a nossa parte (%) = comissão ÷ (valor dos produtos − taxa). Tudo só
 * com os pedidos pagos: a venda do período reembolsada depois não tem taxa nem comissão, então o
 * valor dela também fica fora (senão a parte dos coprodutores cresce pelo valor reembolsado).
 */
export interface CoproSplit {
  commission: number;
  partners: number;
  /** Nossa parte do que sobra depois da taxa da Kiwify; null sem venda. */
  share: number | null;
  /** Juros do parcelamento pagos pelos compradores: fora do faturamento, só informação. */
  interest: number;
}

/** Somas dos pedidos pagos que a divisão usa (valor dos produtos, taxa, comissão e juros). */
export interface PaidSums {
  products: number;
  fee: number;
  commission: number;
  interest: number;
}

export function paidSumsOf(rows: readonly RealOrderRow[]): PaidSums {
  const s: PaidSums = { products: 0, fee: 0, commission: 0, interest: 0 };
  for (const o of rows) {
    if (!isPaid(o)) continue;
    s.products += revenueOf(o);
    s.fee += o.feeCents ?? 0;
    s.commission += o.commissionCents ?? 0;
    s.interest += Math.max(0, o.grossCents - (o.baseCents ?? o.grossCents));
  }
  return s;
}

export function coproSplitOf(t: PaidSums): CoproSplit {
  const afterFee = t.products - t.fee;
  return {
    commission: t.commission,
    partners: Math.max(0, afterFee - t.commission),
    share: afterFee > 0 ? t.commission / afterFee : null,
    interest: t.interest,
  };
}

/** DRE do modo ao vivo: o contrato `PnL` com a divisão da coprodução à parte (só informação). */
/**
 * DRE real. `mediaNote` = por que a mídia não entra (Meta com erro, não conectada, hoje sem leitura
 * ou filtro que a Meta não recorta): aí as linhas da mídia e dos resultados saem e margem/MER ficam
 * sem valor, em vez de um resultado sem o custo da mídia.
 */
export type LivePnL = PnL & { split?: CoproSplit | null; mediaNote?: string | null };

/** Garantia da Kiwify: o comprador pode pedir o dinheiro de volta em até 60 dias. */
export const GUARANTEE_DAYS = 60;
/** Abaixo disso o reembolso medido é ruído: vale o padrão dos Ajustes, com aviso (o mesmo das réguas). */
const MIN_REFUND_SAMPLE = 30;

/** Segunda-feira (00:00 em São Paulo) da semana de um instante. */
export function weekStartSP(at: number): number {
  const day = Math.floor((at - dayStart('1970-01-01')) / DAY);
  // 01/01/1970 foi quinta-feira: (dia + 3) % 7 = dias desde a segunda.
  return dayStart('1970-01-01') + (day - ((((day + 3) % 7) + 7) % 7)) * DAY;
}

/**
 * Idade mínima de uma venda para entrar na taxa de reembolso: a venda nova ainda não teve tempo de
 * voltar (a garantia é de 60 dias), então medir em todas puxa a taxa para baixo.
 */
export const MATURE_DAYS = 30;

/** Reembolso medido nas vendas com pelo menos `MATURE_DAYS` dias (todos os produtos). */
export interface RefundRate {
  rate: number;
  measured: boolean;
  /** Vendas maduras (com `MATURE_DAYS` dias ou mais): pagas e reembolsadas. */
  paid: number;
  lost: number;
  /** Todas as vendas da lista, maduras ou não (para a nota dizer que ainda são recentes). */
  all: { paid: number; lost: number };
}

/**
 * Reembolso medido: reembolsos e chargebacks ÷ (pagos + reembolsados), só nas vendas com pelo menos
 * 30 dias. Sem amostra madura (poucas vendas antigas), vale o padrão dos Ajustes (`fallback`).
 */
export function refundRateOf(
  rows: readonly RealOrderRow[],
  fallback: number,
  now: number = Number.POSITIVE_INFINITY,
): RefundRate {
  const matureBy = now - MATURE_DAYS * DAY;
  let paid = 0;
  let lost = 0;
  const all = { paid: 0, lost: 0 };
  for (const o of rows) {
    const p = isPaid(o);
    if (!p && !isRefundLike(o.status)) continue;
    if (p) all.paid++;
    else all.lost++;
    if (o.at > matureBy) continue;
    if (p) paid++;
    else lost++;
  }
  const n = paid + lost;
  return n >= MIN_REFUND_SAMPLE
    ? { rate: lost / n, measured: true, paid, lost, all }
    : { rate: fallback, measured: false, paid, lost, all };
}

/** Nota da taxa usada no reembolso esperado: medida nas vendas maduras ou o padrão, dizendo por quê. */
export function refundRateNote(r: RefundRate): string {
  const n = r.paid + r.lost;
  if (r.measured)
    return `reembolso medido de ${pct1(r.rate)} (${r.lost} de ${n} vendas com ${MATURE_DAYS} dias ou mais)`;
  const total = r.all.paid + r.all.lost;
  const dflt = `reembolso padrão dos Ajustes (${pct1(r.rate)})`;
  if (total < MIN_REFUND_SAMPLE) return `${dflt}: poucas vendas para medir (${total})`;
  return `${dflt}: as vendas ainda são recentes, só ${n} de ${total} têm ${MATURE_DAYS} dias ou mais e o reembolso aparece com o tempo · até agora voltaram ${r.all.lost} (${pct1(r.all.lost / total)})`;
}

/** Lucro em risco por semana de venda (segunda a domingo, São Paulo), com os pedidos reais. */
export interface AtRiskWeeks {
  t: number[];
  /** Vendas da semana (pagas e as reembolsadas depois), no valor dos produtos. */
  revenue: number[];
  /** O que segue pago e dentro dos 60 dias da garantia. */
  atRisk: number[];
  /** Em garantia × reembolso medido. */
  expected: number[];
}

/**
 * Semanas desde a primeira venda dentro da garantia até a semana de agora (no máximo as 9 semanas
 * que cabem nos 60 dias): nada antes do primeiro pedido real, nada preenchido pela simulação.
 */
export function atRiskWeeks(rows: readonly RealOrderRow[], now: number, rate: number): AtRiskWeeks {
  const since = now - GUARANTEE_DAYS * DAY;
  const sold = rows.filter((o) => o.at > since && o.at <= now && (isPaid(o) || isRefundLike(o.status)));
  const out: AtRiskWeeks = { t: [], revenue: [], atRisk: [], expected: [] };
  if (sold.length === 0) return out;
  const first = weekStartSP(Math.min(...sold.map((o) => o.at)));
  for (let w = first; w <= now; w += 7 * DAY) {
    out.t.push(w);
    out.revenue.push(0);
    out.atRisk.push(0);
  }
  for (const o of sold) {
    const i = Math.round((weekStartSP(o.at) - first) / (7 * DAY));
    if (i < 0 || i >= out.t.length) continue;
    out.revenue[i] = (out.revenue[i] ?? 0) + revenueOf(o);
    if (isPaid(o)) out.atRisk[i] = (out.atRisk[i] ?? 0) + revenueOf(o);
  }
  out.expected = out.atRisk.map((v) => Math.round(v * rate));
  return out;
}

/** Pedido com a liberação prevista pela Kiwify (`funds_status` e `estimated_deposit_date`), se o servidor mandar. */
export type DepositRow = RealOrderRow & { depositAt?: number | null; fundsStatus?: string | null };

/** Caixa do modo ao vivo: saldo e fôlego dependem do extrato da Kiwify, que ainda não chega ao painel. */
export interface LiveCash {
  available: null;
  /** Nossa comissão dos pedidos pagos com liberação prevista para depois de agora; null sem a previsão. */
  pending: number | null;
  runwayDays: null;
  calendar: { dateKey: string; cents: number }[];
  /** Motivo em português simples do que não é medido. */
  notMeasured: string;
}

export const NO_STATEMENT = 'Sem extrato da Kiwify: o saldo e o fôlego de caixa ainda não chegam ao painel.';
export const NO_DEPOSITS =
  'Sem extrato da Kiwify: o saldo, o que falta liberar e as datas de liberação ainda não chegam ao painel.';

/**
 * "A liberar" = a nossa comissão dos pedidos pagos (nunca Pix ou boleto ainda não pagos) cuja data
 * prevista de liberação é depois de agora; calendário dos próximos 15 dias em São Paulo. Sem a data
 * prevista nos pedidos (servidor antigo), nada é estimado: tudo fica "não medido".
 */
export function cashOf(rows: readonly DepositRow[], now: number): LiveCash {
  const known = rows.some((o) => o.depositAt !== undefined);
  if (!known)
    return { available: null, pending: null, runwayDays: null, calendar: [], notMeasured: NO_DEPOSITS };
  const today = todayKey(now);
  const keys = Array.from({ length: 15 }, (_, i) => todayKey(dayStart(today) + i * DAY + DAY / 2));
  const byDay = new Map<string, number>(keys.map((k) => [k, 0]));
  let pending = 0;
  for (const o of rows) {
    if (!isPaid(o) || o.depositAt == null || o.depositAt <= now) continue;
    if (o.fundsStatus && o.fundsStatus !== 'waiting') continue;
    const c = o.commissionCents ?? 0;
    pending += c;
    const k = todayKey(o.depositAt);
    if (byDay.has(k)) byDay.set(k, (byDay.get(k) ?? 0) + c);
  }
  return {
    available: null,
    pending,
    runwayDays: null,
    calendar: keys.map((dateKey) => ({ dateKey, cents: byDay.get(dateKey) ?? 0 })),
    notMeasured: NO_STATEMENT,
  };
}

/** Base da conta: o período escolhido, os últimos 28 dias (período sem comprador) ou nenhum comprador. */
export type EconomicsBasis = 'periodo' | 'ultimos28d' | 'sem_compradores';

/**
 * Valor do comprador do modo ao vivo: o contrato `BuyerValue` com a base da conta e a nota que a tela
 * mostra. Sem comprador do curso nem nos últimos 28 dias, os valores ficam nulos (nunca o simulado).
 */
export type LiveBuyerValue = BuyerValue & {
  basis: EconomicsBasis;
  note: string;
  /**
   * O que fica na empresa por venda de cada produto (valor do produto − taxa da Kiwify, média dos
   * pedidos pagos): o simulador do Plano parte destes valores, não do líquido dos Ajustes.
   */
  unit?: { front: number; upsell: number | null };
  /**
   * Taxa da Kiwify dos Ajustes: o degrau do simulador (produto que ainda não existe, sem pedido para
   * tirar a média) entra pelo mesmo critério, preço − taxa, e não pela parte da coprodução.
   */
  kiwifyFee?: { pct: number; fixed: number };
  /** Consultorias fechadas ainda não têm registro real: o `b.closed` 0 não é uma medida. */
  backendMeasured?: false;
};

/** Taxa padrão da Kiwify (8,99% + R$ 2,49) quando os Ajustes ainda não chegaram. */
const KIWIFY_FEE_DEFAULT = { pct: 0.0899, fixed: 249 } as const;

/** Janela fixa da reserva e do reembolso: hoje e os 27 dias antes (o mesmo início o dia todo). */
const WINDOW_DAYS = 28;

const FALLBACK_ECONOMICS: Pick<
  EconomicsV2,
  'cpaCeilingBasis' | 'testBudgetFactor' | 'bestOfWeekRange' | 'frozen'
> = {
  cpaCeilingBasis: 'receita_no_ato',
  testBudgetFactor: 0.5,
  bestOfWeekRange: [0.75, 1],
  frozen: null,
};

/** KPIs de parâmetro (régua) que o snapshot e o detalhe da métrica recebem da conta real. */
const PARAM_IDS = [
  'cpaTarget',
  'cpaBreakEven',
  'roasTarget',
  'roasBreakEven',
  'merTarget',
  'merBreakEven',
  'roasRealTarget',
  'roiTarget',
  'cpiTarget',
  'rpvBreakEven',
] as const satisfies readonly KpiId[];
type ParamId = (typeof PARAM_IDS)[number];

/** Lucro em risco (FIN-04) e caixa (FIN-05): sempre da conta real, nunca do snapshot simulado. */
const RISK_CASH_IDS = [
  'revenueAtRisk',
  'projectedRefunds',
  'cashAvailable',
  'cashPending',
  'runwayDays',
] as const satisfies readonly KpiId[];
type RiskCashId = (typeof RISK_CASH_IDS)[number];

/** Metas do dia (Hoje, TV e Ajustes): da meta mensal salva com as vendas reais, nunca da simulação. */
const GOAL_IDS = [
  'goalRevenueDay',
  'goalSalesDay',
  'goalSpendDay',
  'paceRevenue',
  'projectionRevenueEod',
  'goalProgressRevenue',
  'goalProgressSales',
  'goalProgressProfit',
] as const satisfies readonly KpiId[];
type GoalId = (typeof GOAL_IDS)[number];

export const NO_GOAL = 'sem meta do mês: defina em Ajustes › Metas';
export const GOAL_TODAY_ONLY = 'a meta do dia vale só para hoje, sem recorte de campanha ou anúncio';

/** Recorte de campanha, conjunto ou anúncio: a meta do dia é da operação inteira (como na simulação). */
const adLevelFilter = (q: Query): boolean =>
  !!(q.filters.campaignIds?.length || q.filters.adsetIds?.length || q.filters.adIds?.length);

export function financeModule(ctx: RealCtx): RealModule {
  const { clock, provider, v2 } = ctx;

  /**
   * Ajustes › Economia: os do boot com o que o dono salvou no servidor por cima (`realSettings.ts`);
   * antes da abertura, os da simulação. O valor do comprador simulado nunca é usado.
   */
  async function econSettings(q: Query): Promise<{ settings: EconomicsV2 | null; sim: Economics | null }> {
    const sim = v2.getEconomics ? await (v2.getEconomics as GetEconomics)(q).catch(() => null) : null;
    return { settings: ctx.settings()?.economicsV2 ?? sim?.settings ?? null, sim };
  }

  /** Pedidos do período (com os filtros) e dos 28 dias até agora (reserva e reembolso). */
  async function rowsFor(q: Query) {
    const now = clock();
    const r = rangeMs(q, now);
    const w = {
      from: dayStart(todayKey(now)) - (WINDOW_DAYS - 1) * DAY,
      to: Math.ceil(now / 60_000) * 60_000,
    };
    const [period, last28] = await Promise.all([ctx.load(r.from, r.to), ctx.load(w.from, w.to)]);
    return {
      r,
      w,
      period: period.filter((o) => matches(o, q)),
      last28: last28.filter((o) => matches(o, q)),
    };
  }

  /** Reembolso medido em 28 dias (curso e 20X, pela data da venda); amostra pequena = padrão dos Ajustes. */
  function refundOf(rows: readonly RealOrderRow[]): { rate: number; note: string } {
    let paid = 0;
    let lost = 0;
    for (const o of rows) {
      if (o.kind !== 'front' && o.kind !== 'upsell') continue;
      if (isPaid(o)) paid++;
      // O servidor grava "chargedback": as duas grafias contam (como no resto do modo ao vivo).
      else if (isRefundLike(o.status)) lost++;
    }
    const n = paid + lost;
    if (n >= MIN_REFUND_SAMPLE) return { rate: lost / n, note: `reembolso de ${pct1(lost / n)} em 28 dias` };
    const dflt = ctx.settings()?.economics?.refundRateDefault ?? 0.08;
    return { rate: dflt, note: `reembolso padrão dos Ajustes (${pct1(dflt)}): poucas vendas em 28 dias` };
  }

  /** Custo real da mídia ÷ gasto (o fator do servidor); sem custo, o imposto dos Ajustes por dentro. */
  function multOf(m: RealMedia | null): number {
    const spend = m?.totals.spendCents ?? 0;
    const cost = m?.totals.costCents ?? 0;
    if (spend > 0 && cost > 0) return cost / spend;
    return 1 / (1 - ctx.metaTaxPct());
  }

  interface RealEconomics {
    basis: EconomicsBasis;
    /** Vendas do período escolhido (os KPIs do período nunca usam a reserva). */
    period: BuyerBase;
    /** Vendas da base usada na régua (o período ou os 28 dias). */
    base: BuyerBase;
    /** 20X nos 28 dias: preço líquido do 20X para os cenários quando o período não teve 20X. */
    last28: BuyerBase;
    ruler: Ruler | null;
    refund: { rate: number; note: string };
    mult: number;
    /** Imposto sobre a receita dos Ajustes: só repassado ao `unit.t` (a régua ao vivo não usa). */
    revenueTaxPct: number;
    targetMargin: number;
    settings: EconomicsV2 | null;
    sim: Economics | null;
    note: string;
  }

  /** A conta real inteira de um período: base, régua e a nota que diz de onde vem. */
  async function realEconomics(q: Query): Promise<RealEconomics> {
    const [{ settings, sim }, rows] = await Promise.all([econSettings(q), rowsFor(q)]);
    const period = buyerBase(rows.period);
    const last28 = buyerBase(rows.last28);
    const basis: EconomicsBasis =
      period.front > 0 ? 'periodo' : last28.front > 0 ? 'ultimos28d' : 'sem_compradores';
    const base = basis === 'periodo' ? period : last28;
    const media =
      basis === 'periodo'
        ? await ctx.loadMedia(rows.r.from, rows.r.to).catch(() => null)
        : await ctx.loadMedia(rows.w.from, rows.w.to).catch(() => null);
    const refund = refundOf(rows.last28);
    const mult = multOf(media);
    const revenueTaxPct = ctx.settings()?.costs?.revenueTaxPct ?? 0;
    const targetMargin = ctx.settings()?.goals?.targetMargin ?? 0.2;
    const ruler = deriveRuler({
      base,
      refundRate: refund.rate,
      mediaMult: mult,
      targetMargin,
      ceilingBasis: (settings ?? FALLBACK_ECONOMICS).cpaCeilingBasis,
    });
    const who =
      basis === 'periodo'
        ? `${plural(period.front, 'comprador', 'compradores')} do curso no período`
        : basis === 'ultimos28d'
          ? `sem compradores do curso no período; usando os últimos 28 dias (${plural(last28.front, 'comprador', 'compradores')})`
          : 'sem compradores do curso ainda';
    const note =
      basis === 'sem_compradores'
        ? who
        : `valor dos produtos menos a taxa da Kiwify, por comprador do curso · ${who} · ${refund.note}`;
    return {
      basis,
      period,
      base,
      last28,
      ruler,
      refund,
      mult,
      revenueTaxPct,
      targetMargin,
      settings,
      sim,
      note,
    };
  }

  /** `BuyerValue` de um valor no ato (o real ou o de um cenário), com a mesma régua. */
  function valueAt(
    x: RealEconomics,
    e: Pick<EconomicsV2, 'testBudgetFactor' | 'bestOfWeekRange' | 'cpaCeilingBasis'>,
    atSale: number,
    u: { rate: number; buyers: number; takers: number },
    contribution: number,
  ): BuyerValue {
    const ceilingReal = Math.round((atSale * (1 - x.refund.rate)) / x.mult);
    return {
      atSale,
      withBackend: null,
      u: { ...u, ci90: wilson90(u.takers, u.buyers) },
      d: null,
      b: { closed: 0, frontBuyers: u.buyers, perFrontBuyerGross: 0, ci90: null },
      upsellContribution: contribution,
      ceilingAtSale: e.cpaCeilingBasis === 'contribuicao' ? ceilingReal : atSale,
      ceilingReal,
      testBudgetDay: Math.round(atSale * e.testBudgetFactor),
      bestOfWeekDay: [Math.round(atSale * e.bestOfWeekRange[0]), Math.round(atSale * e.bestOfWeekRange[1])],
      frozenUntil: x.sim?.value.frozenUntil ?? null,
    };
  }

  /**
   * Valor do comprador real (Financeiro, Hoje, Plano, Ajustes): o período escolhido; sem comprador do
   * curso no período, os últimos 28 dias, com a nota dizendo isso; sem comprador nenhum, vazio.
   */
  async function getEconomics(q: Query): ReturnType<GetEconomics> {
    const x = await realEconomics(q);
    const e = { ...FALLBACK_ECONOMICS, ...(x.settings ?? {}) };
    const settings = (x.settings ?? x.sim?.settings) as EconomicsV2;
    const b = x.base;
    if (!x.ruler) {
      // Nenhum comprador do curso, nem nos últimos 28 dias: nada para medir (e nada simulado). O IC
      // fica no formato do contrato — sem amostra, Wilson dá 0% a 100% — porque as telas leem ci90[0].
      const empty = {
        atSale: null,
        withBackend: null,
        u: { rate: null, buyers: 0, takers: 0, ci90: wilson90(0, 0) },
        d: null,
        b: { closed: 0, frontBuyers: 0, perFrontBuyerGross: 0, ci90: null },
        upsellContribution: null,
        ceilingAtSale: null,
        ceilingReal: null,
        testBudgetDay: null,
        bestOfWeekDay: null,
        frozenUntil: x.sim?.value.frozenUntil ?? null,
        basis: x.basis,
        note: x.note,
      } as unknown as LiveBuyerValue;
      return { settings, value: empty, scenarios: [] };
    }
    const u = { rate: b.upsell / b.front, buyers: b.front, takers: b.upsell };
    // Cenários com o preço líquido real de cada produto (média dos pedidos; sem 20X no período, o dos
    // 28 dias). Hipóteses rotuladas: "Só curso", o medido e "20X a 15%".
    const front = b.frontValue / b.front;
    const upsellUnit =
      b.upsell > 0
        ? b.upsellValue / b.upsell
        : x.last28.upsell > 0
          ? x.last28.upsellValue / x.last28.upsell
          : null;
    const value: LiveBuyerValue = {
      ...valueAt(x, e, x.ruler.atSale, u, upsellShare(b) ?? 0),
      basis: x.basis,
      note: x.note,
      unit: { front: Math.round(front), upsell: upsellUnit == null ? null : Math.round(upsellUnit) },
      kiwifyFee: {
        pct: ctx.settings()?.costs?.kiwifyFeePct ?? KIWIFY_FEE_DEFAULT.pct,
        fixed: ctx.settings()?.costs?.kiwifyFeeFixed ?? KIWIFY_FEE_DEFAULT.fixed,
      },
      backendMeasured: false,
    };
    const at = (rate: number) => Math.round(front + rate * (upsellUnit ?? 0));
    const share = (rate: number) => (front > 0 && upsellUnit != null ? (rate * upsellUnit) / front : 0);
    // O cenário medido diz de onde vem: "Hoje" quando o período é o dia de hoje (o Plano pede hoje).
    const isToday = q.range.from === q.range.to && q.range.to === todayKey(clock());
    const label = x.basis === 'periodo' ? (isToday ? 'Hoje' : 'No período') : 'Últimos 28 dias';
    const scenarios: Economics['scenarios'] = [
      { label: 'Só curso', u: 0, value: valueAt(x, e, at(0), { ...u, rate: 0 }, 0) },
      { label: `${label} (${b.upsell}/${b.front})`, u: u.rate, value },
      ...(upsellUnit != null
        ? [{ label: '20X a 15%', u: 0.15, value: valueAt(x, e, at(0.15), { ...u, rate: 0.15 }, share(0.15)) }]
        : []),
    ];
    return { settings, value, scenarios };
  }

  /** Régua real em KPIs (snapshot e detalhe da métrica): sem comprador, sem régua (e sem cor). */
  function paramKpis(x: RealEconomics): Record<ParamId, KpiValue> {
    const now = clock();
    const fresh = ctx.kiwiFresh(now);
    const ru = x.ruler;
    const none = 'sem compradores do curso para medir a régua';
    const set = (id: ParamId, v: number | null | undefined, note: string, missing = none): KpiValue =>
      v == null || !Number.isFinite(v)
        ? noRealKpi(id, ru ? missing : none)
        : { ...kv(id, v, null, now), freshness: fresh, note };
    const be = `empate do resultado da empresa (${x.note})`;
    const target = `deixa a margem-alvo de ${pct1(x.targetMargin)} sobre o faturamento`;
    const noMargin = `a margem-alvo de ${pct1(x.targetMargin)} come todo o resultado`;
    return {
      cpaBreakEven: set('cpaBreakEven', ru?.cpaBreakEven, `gasto por comprador no ${be}`),
      cpaTarget: set('cpaTarget', ru?.cpaTarget, `gasto por comprador que ${target}`, noMargin),
      roasBreakEven: set('roasBreakEven', ru?.roasBreakEven, `valor ÷ gasto no ${be}`),
      roasTarget: set('roasTarget', ru?.roasTarget, `valor ÷ gasto que ${target}`, noMargin),
      merBreakEven: set('merBreakEven', ru?.merBreakEven, `faturamento ÷ custo de mídia no ${be}`),
      merTarget: set('merTarget', ru?.merTarget, `faturamento ÷ custo de mídia que ${target}`, noMargin),
      roasRealTarget: set(
        'roasRealTarget',
        ru?.roasRealTarget,
        `resultado antes da mídia ÷ custo que ${target}`,
        noMargin,
      ),
      // Sem medição real por trás: ficam vazios (sem cor) em vez do número da simulação.
      roiTarget: noRealKpi('roiTarget', 'alvo do ROI ainda não definido com os números reais'),
      cpiTarget: noRealKpi('cpiTarget', 'não medido: falta a conversão real do checkout'),
      rpvBreakEven: noRealKpi('rpvBreakEven', 'não medido: falta a contagem real de visitas ao quiz'),
    };
  }

  /** Unit economics da DRE com a régua real (u, r e alvos medidos; o que não dá para medir, nulo). */
  function realUnit(x: RealEconomics, base: UnitEconomics | undefined): UnitEconomics {
    const ru = x.ruler;
    const b = x.base;
    const unit = {
      u: b.front > 0 ? b.upsell / b.front : null,
      r: x.refund.rate,
      m: x.targetMargin,
      t: x.revenueTaxPct,
      measuredWindowDays: x.basis === 'periodo' ? 0 : WINDOW_DAYS,
      cpaBreakEven: ru?.cpaBreakEven ?? null,
      cpaTarget: ru?.cpaTarget ?? null,
      roasBreakEven: ru?.roasBreakEven ?? null,
      roasTarget: ru?.roasTarget ?? null,
      contributionPerBuyer: ru?.contribution ?? null,
      cpiTarget: null,
      roasRealTarget: ru?.roasRealTarget ?? null,
      merBreakEven: ru?.merBreakEven ?? null,
      merTarget: ru?.merTarget ?? null,
      roiTarget: null,
      rpvBreakEven: null,
      checkoutConv7d: null,
      // Os padrões saem só dos Ajustes (preços e taxas padrão), não do mundo simulado.
      defaults: base?.defaults ?? null,
    };
    return unit as unknown as UnitEconomics;
  }

  async function getFinance(q: FinanceQuery): Promise<FinanceReport> {
    const [base, real, econ] = await Promise.all([
      provider.getFinance(q),
      ctx.realKpis(q),
      realEconomics(q).catch(() => null),
    ]);
    // Sem os pedidos (nenhuma leitura boa ainda): a DRE é feita de vendas; erro da fonte, nunca zeros.
    if (real.ordersDown)
      throw Object.assign(new Error('Vendas indisponíveis (erro no servidor): tente de novo'), {
        code: 'source_unavailable',
      });
    const media = (x: RealMedia | null) => (x ? ctx.costOf(x) : 0);
    const now = clock();
    const fr = rangeMs(q, now);
    const fc = compareMs(q, now);
    const [fm, fmc, rows, crows] = await Promise.all([
      // Falha da Meta não derruba a DRE: as linhas da mídia saem com o motivo (`noteOf`).
      ctx.loadMedia(fr.from, fr.to).catch(() => null),
      fc ? ctx.loadMedia(fc.from, fc.to).catch(() => null) : Promise.resolve(null),
      // Pedidos do período (e da comparação) para a divisão da coprodução, só com os pagos.
      ctx.load(fr.from, fr.to).catch(() => null),
      fc ? ctx.load(fc.from, fc.to).catch(() => null) : Promise.resolve(null),
    ]);
    // Filtro fora da Meta (Pix, produto…): as vendas vêm recortadas e a mídia é a da conta inteira.
    const offMeta = offMetaDims(q.filters).length > 0;
    // Filtro de campanha/conjunto/anúncio: a mídia é recortada e as vendas não (Kiwify não separa).
    const byEntity = hasEntityFilter(q.filters);
    const noteOf = (m: RealMedia | null, range: { from: number; to: number }): string | null => {
      if (offMeta) return OFF_META_NOTE;
      if (byEntity) return ENTITY_SALES_NOTE;
      const miss = mediaMissing(ctx.loadMedia, m, now, range);
      return miss ? `Mídia na Meta: ${miss}` : null;
    };
    const splitOf = (list: RealOrderRow[] | null) =>
      list ? coproSplitOf(paidSumsOf(list.filter((o) => matches(o, q)))) : null;
    const s = ctx.settings();
    const expenses = s?.costs?.expenses ?? [];
    // Imposto sobre a receita só quando o dono cadastra a taxa (Ajustes › Custos); o ao vivo começa em 0.
    const taxPct = s?.costs?.revenueTaxPct ?? 0;
    const taxOnCommission = (s?.economicsV2?.revenueTaxBasis ?? 'gross') === 'own_commission';
    const pnl = (
      t: Totals,
      m: RealMedia | null,
      range: { from: number; to: number },
      split: CoproSplit | null,
      mediaNote: string | null,
    ): LivePnL => {
      // A mídia é paga pela empresa com o faturamento inteiro (antes da divisão com os coprodutores),
      // então a DRE fecha no resultado da empresa e não desconta a mídia só da nossa comissão.
      const mediaCost = media(m);
      const tax = taxPct > 0 ? Math.round(taxPct * (taxOnCommission ? t.commission : t.gross)) : 0;
      // Faturamento = valor dos produtos: os juros do parcelamento nunca entram (ficam com a Kiwify).
      const contribution = t.gross - t.fee - tax - t.refundsGross - mediaCost;
      const fixed = fixedCostsIn(expenses, range.from, range.to);
      const pct = (taxPct * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
      const full: LivePnL = {
        lines: [
          { id: 'gross', label: 'Faturamento (valor dos produtos)', cents: t.gross, subtotal: false },
          { id: 'fee', label: 'Taxa da Kiwify', cents: -t.fee, subtotal: false },
          ...(tax > 0
            ? [
                {
                  id: 'tax' as const,
                  label: `Imposto sobre a receita (${pct}% ${taxOnCommission ? 'da nossa comissão' : 'do faturamento'})`,
                  cents: -tax,
                  subtotal: false,
                },
              ]
            : []),
          { id: 'refunds', label: 'Reembolsos', cents: -t.refundsGross, subtotal: false },
          {
            id: 'media',
            label: m ? 'Mídia na Meta (com imposto)' : 'Mídia (aguardando conexão com a Meta)',
            cents: -mediaCost,
            subtotal: false,
          },
          {
            id: 'contribution',
            label: 'Resultado da empresa depois da mídia',
            cents: contribution,
            subtotal: true,
          },
          {
            id: 'fixed',
            label: expenses.length ? 'Despesas fixas (rateio diário)' : 'Despesas fixas (não cadastradas)',
            cents: fixed > 0 ? -fixed : 0,
            subtotal: false,
          },
          {
            id: 'operating',
            label: 'Resultado depois das despesas fixas',
            cents: contribution - fixed,
            subtotal: true,
          },
        ],
        margin: t.gross > 0 ? contribution / t.gross : null,
        mer: mediaCost > 0 ? t.gross / mediaCost : null,
        // A base "coorte" ainda não existe com os pedidos reais: a DRE é sempre por data do evento
        // (vendas pela data da venda, reembolsos pela data do reembolso).
        basis: 'event_date',
        source: 'real',
        // Nossa comissão e a parte dos coprodutores, medidas nos pedidos pagos: informação, fora do resultado.
        split,
      };
      // Sem a mídia real (ou com filtro que a Meta não recorta): nada de resultado sem o custo da mídia.
      if (!mediaNote) return full;
      return {
        ...full,
        lines: full.lines.filter((l) => !['media', 'contribution', 'fixed', 'operating'].includes(l.id)),
        margin: null,
        mer: null,
        mediaNote,
      };
    };
    const cash = cashOf((await guaranteeRows(q, now).catch(() => null)) ?? [], now);
    return {
      ...base,
      pnl: pnl(real.t, fm, fr, splitOf(rows), noteOf(fm, fr)),
      compare: real.c && fc ? pnl(real.c, fmc, fc, splitOf(crows), noteOf(fmc, fc)) : null,
      // Alvos da DRE (margem, MER, ROAS real) com a régua real, nunca a da simulação.
      unit: econ ? realUnit(econ, base.unit) : realUnit(emptyEconomics(), base.unit),
      // Saldo e fôlego dependem do extrato da Kiwify: vazios com o motivo (nunca R$ 0 nem a simulação).
      // "A liberar" é a nossa comissão já paga com liberação prevista; Pix e boleto não pagos ficam fora.
      cash: cash as unknown as FinanceReport['cash'],
    };
  }

  /** Imposto sobre a receita cadastrado (0 sem cadastro), na base escolhida em Ajustes › Economia. */
  function revenueTaxOf(t: Pick<Totals, 'gross' | 'commission'>): number {
    const s = ctx.settings();
    const taxPct = s?.costs?.revenueTaxPct ?? 0;
    const onCommission = (s?.economicsV2?.revenueTaxBasis ?? 'gross') === 'own_commission';
    return taxPct > 0 ? Math.round(taxPct * (onCommission ? t.commission : t.gross)) : 0;
  }

  /**
   * Metas do dia com as vendas reais (`realGoals.ts`): a meta mensal salva em Ajustes › Metas, o
   * faturamento de hoje (valor dos produtos), os cursos pagos e o resultado da empresa (a conta da
   * DRE; sem a Meta, sem lucro). Sem meta salva (0), fora de hoje ou com recorte de anúncio: nenhuma.
   */
  async function liveGoals(q: Query, t: Totals, media: RealMedia | null): Promise<GoalProgress | null> {
    const s = ctx.settings();
    const now = clock();
    if (!s?.goals || !(s.goals.monthlyRevenue > 0)) return null;
    const today = todayKey(now);
    if (q.range.from !== today || q.range.to !== today || adLevelFilter(q)) return null;
    // Os 27 dias antes de hoje (a mesma consulta da reserva e do reembolso, que já fica em cache).
    const rows = await ctx
      .load(dayStart(today) - (WINDOW_DAYS - 1) * DAY, Math.ceil(now / 60_000) * 60_000)
      .catch(() => [] as RealOrderRow[]);
    const profit = media ? t.gross - t.fee - revenueTaxOf(t) - t.refundsGross - ctx.costOf(media) : null;
    return buildLiveGoals({
      now,
      goals: s.goals,
      checkpoints: s.checkpoints ?? [],
      actual: { revenue: t.gross, sales: t.front, profit },
      history: goalHistory(
        rows.filter((o) => matches(o, q)),
        now,
      ),
      priceFront: s.economics?.priceFront ?? 0,
    });
  }

  /** As metas do dia em KPIs (Hoje, TV, Ajustes e o detalhe da métrica); sem meta, vazios com o motivo. */
  async function goalKpis(q: Query, now: number, t: Totals): Promise<Record<GoalId, KpiValue>> {
    const r = rangeMs(q, now);
    const media = await ctx.loadMedia(r.from, r.to).catch(() => null);
    const g = await liveGoals(q, t, media).catch(() => null);
    const saved = (ctx.settings()?.goals?.monthlyRevenue ?? 0) > 0;
    const why = saved ? GOAL_TODAY_ONLY : NO_GOAL;
    if (!g)
      return Object.fromEntries(GOAL_IDS.map((id) => [id, noRealKpi(id, why)])) as Record<GoalId, KpiValue>;
    const fresh = ctx.kiwiFresh(now);
    const set = (id: GoalId, v: number | null, missing: string, note?: string): KpiValue =>
      v == null || !Number.isFinite(v)
        ? noRealKpi(id, missing)
        : { ...kv(id, v, null, now), freshness: fresh, ...(note ? { note } : {}) };
    // Investimento planejado = vendas da meta × CPA alvo real (a mesma régua do Hoje).
    const x = await realEconomics(q).catch(() => null);
    const cpa = x?.ruler?.cpaTarget ?? null;
    const ratio = (l: { goal: number; actual: number } | null) =>
      l && l.goal > 0 ? l.actual / l.goal : null;
    const meta = 'meta do mês salva em Ajustes › Metas, pelo peso do dia da semana';
    return {
      goalRevenueDay: set('goalRevenueDay', g.revenue.goal, NO_GOAL, meta),
      goalSalesDay: set('goalSalesDay', g.sales.goal, 'sem ticket do curso para dividir a meta'),
      goalSpendDay: set(
        'goalSpendDay',
        cpa == null || g.sales.goal <= 0 ? null : Math.round(g.sales.goal * cpa),
        'sem compradores do curso para medir o CPA alvo',
        'vendas da meta × CPA alvo medido nas vendas reais',
      ),
      paceRevenue: set(
        'paceRevenue',
        g.revenue.expectedNow > 0 ? g.revenue.pace : null,
        'cedo demais para medir o ritmo',
      ),
      projectionRevenueEod: set('projectionRevenueEod', g.projection.revenue, NO_GOAL),
      goalProgressRevenue: set('goalProgressRevenue', ratio(g.revenue), NO_GOAL),
      goalProgressSales: set('goalProgressSales', ratio(g.sales), 'sem ticket do curso para dividir a meta'),
      goalProgressProfit: set(
        'goalProgressProfit',
        ratio(g.profit),
        'sem o custo da Meta para medir o lucro',
      ),
    };
  }

  /** Pedidos vendidos dentro da garantia (60 dias até agora), com os filtros: lucro em risco e caixa. */
  async function guaranteeRows(q: Query, now: number): Promise<DepositRow[]> {
    const from = dayStart(todayKey(now)) - GUARANTEE_DAYS * DAY;
    const rows = await ctx.load(from, Math.ceil(now / 60_000) * 60_000);
    return rows.filter((o) => matches(o, q));
  }

  /** Lucro em risco e caixa do momento (não mudam com o período), com a nota de onde vem cada um. */
  async function riskAndCash(q: Query, now: number) {
    const rows = await guaranteeRows(q, now);
    const fallback = ctx.settings()?.economics?.refundRateDefault ?? 0.08;
    const since = now - GUARANTEE_DAYS * DAY;
    // Taxa só nas vendas com 30 dias ou mais (a venda nova ainda não teve tempo de voltar).
    const refund = refundRateOf(
      rows.filter((o) => o.at > since),
      fallback,
      now,
    );
    const weeks = atRiskWeeks(rows, now, refund.rate);
    const atRisk = weeks.atRisk.reduce((a, v) => a + v, 0);
    const rateNote = refundRateNote(refund);
    return { weeks, atRisk, expected: Math.round(atRisk * refund.rate), rateNote, cash: cashOf(rows, now) };
  }

  /** Conta vazia (a leitura das vendas falhou): a DRE fica sem alvos em vez de herdar a simulação. */
  function emptyEconomics(): RealEconomics {
    const zero: BuyerBase = { front: 0, upsell: 0, frontValue: 0, upsellValue: 0, products: 0 };
    return {
      basis: 'sem_compradores',
      period: zero,
      base: zero,
      last28: zero,
      ruler: null,
      refund: { rate: ctx.settings()?.economics?.refundRateDefault ?? 0.08, note: '' },
      mult: 1 / (1 - ctx.metaTaxPct()),
      revenueTaxPct: ctx.settings()?.costs?.revenueTaxPct ?? 0,
      targetMargin: ctx.settings()?.goals?.targetMargin ?? 0.2,
      settings: null,
      sim: null,
      note: 'sem compradores do curso ainda',
    };
  }

  /** Compradores e valores do período de comparação (mesmos filtros). */
  async function compareBase(q: Query): Promise<BuyerBase | null> {
    const cr = compareMs(q, clock());
    if (!cr) return null;
    const rows = await ctx.load(cr.from, cr.to).catch(() => null);
    return rows ? buyerBase(rows.filter((o) => matches(o, q))) : null;
  }

  const noBuyers = 'sem compradores do curso no período';

  /** Lucro em risco e caixa em KPIs (snapshot, detalhe da métrica e stream): reais ou vazios com o motivo. */
  async function riskCashKpis(q: Query, now: number): Promise<Record<RiskCashId, KpiValue>> {
    const x = await riskAndCash(q, now).catch(() => null);
    const fresh = ctx.kiwiFresh(now);
    const val = (id: RiskCashId, v: number, note: string): KpiValue => ({
      ...kv(id, v, null, now),
      freshness: fresh,
      note,
    });
    const failed = 'não deu para ler os pedidos da Kiwify agora';
    return {
      revenueAtRisk: x
        ? val(
            'revenueAtRisk',
            x.atRisk,
            'vendas pagas ainda dentro dos 60 dias da garantia (valor dos produtos)',
          )
        : noRealKpi('revenueAtRisk', failed),
      projectedRefunds: x
        ? val('projectedRefunds', x.expected, `valor em garantia × ${x.rateNote}`)
        : noRealKpi('projectedRefunds', failed),
      cashAvailable: noRealKpi('cashAvailable', NO_STATEMENT),
      runwayDays: noRealKpi('runwayDays', NO_STATEMENT),
      cashPending:
        x?.cash.pending != null
          ? val(
              'cashPending',
              x.cash.pending,
              'nossa comissão dos pedidos pagos, pela data prevista de liberação',
            )
          : noRealKpi('cashPending', x ? NO_DEPOSITS : failed),
    };
  }

  /** FIN-04: série por semana de venda com os pedidos reais (nunca as 9 semanas da simulação). */
  async function atRiskSeries(q: Query): Promise<ColumnarSeries> {
    const now = clock();
    const x = await riskAndCash(q, now);
    const { weeks } = x;
    return {
      t: weeks.t,
      series: [
        {
          key: 'revenue',
          label: 'Faturamento da semana',
          role: 'primary',
          colorSlot: 1,
          values: weeks.revenue,
        },
        { key: 'atRisk', label: 'Ainda na garantia', role: 'split', colorSlot: 4, values: weeks.atRisk },
        {
          key: 'expectedRefunds',
          label: 'Reembolso esperado',
          role: 'split',
          colorSlot: 5,
          values: weeks.expected,
        },
      ],
      markers: [],
      freshness: ctx.kiwiFresh(now),
    } as ColumnarSeries;
  }

  /**
   * FIN-03: resultado da empresa acumulado (a mesma conta da DRE, por hora ou por dia): vendas no valor
   * dos produtos − taxa da Kiwify − imposto cadastrado − reembolsos (pela data do reembolso) − custo
   * real da mídia. O último valor bate com o "Resultado da empresa" da DRE; nada vem da simulação.
   */
  async function profitSeries(q: SeriesQuery): Promise<ColumnarSeries> {
    const now = clock();
    const r = rangeMs(q, now);
    const size = q.bucket === '1d' ? DAY : HOUR;
    const [rows, refundRows, m] = await Promise.all([
      ctx.load(r.from, r.to),
      ctx.loadRefunds ? ctx.loadRefunds(r.from, r.to).catch(() => null) : Promise.resolve(null),
      ctx.loadMedia(r.from, r.to).catch(() => null),
    ]);
    const s = ctx.settings();
    const taxPct = s?.costs?.revenueTaxPct ?? 0;
    const taxOnCommission = (s?.economicsV2?.revenueTaxBasis ?? 'gross') === 'own_commission';
    const cur = rows.filter((o) => matches(o, q));
    // Venda (paga ou reembolsada depois) no valor do produto; taxa e imposto da comissão só nas pagas.
    const sale = (o: RealOrderRow): number => {
      const paid = isPaid(o);
      if (!paid && !isRefundLike(o.status)) return 0;
      const base = taxOnCommission ? (paid ? (o.commissionCents ?? 0) : 0) : revenueOf(o);
      return revenueOf(o) - (paid ? (o.feeCents ?? 0) : 0) - taxPct * base;
    };
    // Reembolsos pela data do reembolso (lista do servidor); servidor antigo: pela data da compra.
    const refunds = (refundRows ?? cur)
      .filter((o) => isRefundLike(o.status) && matches(o, q))
      .map((o) => (refundRows ? { ...o, at: o.refundedAt ?? o.at } : o));
    const mult = 1 + ctx.metaTaxPct();
    const cost = (p: { spendCents: number; costCents?: number }) =>
      p.costCents ?? Math.round(p.spendCents * mult);
    const media = m
      ? size === DAY
        ? m.daily.map((d) => ({ ...row0, at: Date.parse(`${d.date}T00:00:00Z`) + BRT, grossCents: cost(d) }))
        : m.hourly.map((p) => ({ ...row0, at: p.t, grossCents: cost(p) }))
      : [];
    const sold = buckets(cur, r.from, r.end, size, now, sale);
    const back = buckets(refunds, r.from, r.end, size, now, revenueOf).values;
    const spent = buckets(media, r.from, r.end, size, now, (o) => o.grossCents).values;
    const step = sold.values.map((v, i) =>
      v == null ? null : Math.round(v - (back[i] ?? 0) - (spent[i] ?? 0)),
    );
    return {
      t: sold.t,
      series: [{ key: 'profit', label: 'Resultado da empresa', role: 'primary', values: cumulative(step) }],
      markers: [],
      freshness: [...ctx.kiwiFresh(now), ...(m ? ctx.metaFresh(now, q.asOf ?? null) : [])],
    } as ColumnarSeries;
  }

  return {
    overrides: { getFinance, ...(v2.getEconomics ? { getEconomics } : {}) },
    // Lucro em risco e caixa: reais (ou vazios com o motivo) no snapshot, no detalhe e no stream.
    // Metas do dia: da meta mensal salva com as vendas reais (ou vazias, com o motivo).
    kpis: async ({ q, now, t }) => ({ ...(await riskCashKpis(q, now)), ...(await goalKpis(q, now, t)) }),
    kpiIds: [...RISK_CASH_IDS, ...GOAL_IDS],
    // A régua real entra no snapshot; a do stream simulado nunca passa por cima dela.
    neverSimKpis: [...PARAM_IDS, ...RISK_CASH_IDS, ...GOAL_IDS],
    series: (q) =>
      q.key === 'atRiskByWeek'
        ? atRiskSeries(q)
        : q.key === 'profitCumulative'
          ? profitSeries(q)
          : Promise.resolve(undefined),
    async kpiDetail(q) {
      if (!(PARAM_IDS as readonly KpiId[]).includes(q.kpi)) return undefined;
      const x = await realEconomics(q);
      const now = clock();
      return {
        value: paramKpis(x)[q.kpi as ParamId],
        series: { t: [], series: [], markers: [], freshness: ctx.kiwiFresh(now) },
        byCampaign: [],
      };
    },
    async patchSnapshot(q, snap, real) {
      // Meta do mês salva: meta do dia, ritmo e projeção com as vendas reais. Sem meta (0), nenhuma: o
      // Hoje mostra "Defina a meta" em vez de uma meta inventada.
      const [x, cmp, goals] = await Promise.all([
        realEconomics(q).catch(() => null),
        compareBase(q),
        liveGoals(q, real.t, real.media).catch(() => null),
      ]);
      snap.goals = goals;
      // Régua de CPA, ROAS, MER e ROAS real (Criativos, Tráfego, Hoje, Financeiro, Ajustes): a real,
      // ou vazia (sem cor) — nunca a da simulação.
      Object.assign(snap.kpis, paramKpis(x ?? emptyEconomics()));
      const k2 = snap.kpisV2;
      if (!k2) return;
      const now = clock();
      const fresh = ctx.kiwiFresh(now) as KpiValueV2['freshness'];
      const period = x?.period ?? null;
      const value = period ? companyBuyerValue(period) : null;
      const frontBuyers = period?.front ?? 0;
      const v2kpi = (id: 'buyerValue' | 'upsellContribution' | 'testBudgetDay', extra: Partial<KpiValueV2>) =>
        ({
          ...(k2[id] ?? { deltaKind: 'relative', status: 'neutral' }),
          id,
          delta: null,
          status: 'neutral',
          freshness: fresh,
          ...extra,
        }) as KpiValueV2;
      k2.buyerValue = v2kpi('buyerValue', {
        value,
        compareValue: cmp ? companyBuyerValue(cmp) : null,
        sample: { n: frontBuyers, min: 1, unit: 'compradores' },
        note:
          value == null
            ? noBuyers
            : `valor dos produtos menos a taxa da Kiwify (curso + 20X), por comprador do curso (${frontBuyers} no período)`,
      }) as NonNullable<typeof k2.buyerValue>;
      // Contribuição do 20X sempre das vendas do período (antes ficava a da simulação, igual em
      // Hoje, Ontem e 7D).
      k2.upsellContribution = v2kpi('upsellContribution', {
        value: period ? upsellShare(period) : null,
        compareValue: cmp ? upsellShare(cmp) : null,
        sample: { n: frontBuyers, min: 30, unit: 'compradores' },
        note:
          frontBuyers > 0
            ? `${period?.upsell ?? 0} de ${frontBuyers} levaram o 20X; o que o 20X deixa na empresa ÷ o que o curso deixa`
            : noBuyers,
      }) as NonNullable<typeof k2.upsellContribution>;
      if (k2.testBudgetDay) {
        const factor = (x?.settings ?? FALLBACK_ECONOMICS).testBudgetFactor;
        k2.testBudgetDay = v2kpi('testBudgetDay', {
          value: value == null ? null : Math.round(value * factor),
          compareValue: null,
          note: value == null ? noBuyers : `${pct1(factor)} do valor do comprador por dia`,
        }) as NonNullable<typeof k2.testBudgetDay>;
      }
    },
    async patchKpisV2(q, out, sim) {
      const x = await realEconomics(q).catch(() => null);
      const kiwi = ctx.kiwiFresh(clock());
      // Sempre gravados (mesmo se a conta falhar): o número simulado nunca passa com o selo da Kiwify.
      const period = x?.period ?? null;
      const value = period ? companyBuyerValue(period) : null;
      const n = period?.front ?? 0;
      const factor = (x?.settings ?? FALLBACK_ECONOMICS).testBudgetFactor;
      setKpiV2(q, out, sim, 'buyerValue', value, kiwi, {
        note:
          value == null
            ? noBuyers
            : `${n} compradores do curso no período; ${period?.upsell ?? 0} levaram o 20X`,
      });
      setKpiV2(q, out, sim, 'testBudgetDay', value == null ? null : Math.round(value * factor), kiwi, {
        note: value == null ? noBuyers : `${pct1(factor)} do valor do comprador por dia`,
      });
      setKpiV2(q, out, sim, 'upsellContribution', period ? upsellShare(period) : null, kiwi, {
        sample: { n, min: 30, unit: 'compradores' },
        ...(n > 0 ? {} : { note: noBuyers }),
      });
    },
  };
}
